import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import axios from 'axios';

import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import settings from '../../constants/constants';

interface PasswordUpdateData {
  currentPassword: string;
  password: string;
}

interface PasswordUpdateResponse {
  id: number;
  message: string;
}

interface PasswordError extends Error {
  error_code?: string;
}

/**
 * Custom hook for updating user password.
 * @returns Mutation object with update password mutation function.
 */
const useUpdatePassword = (): UseMutationResult<AxiosResponse, PasswordError, PasswordUpdateData> => {
  const { data: user } = useUser();

  const { url } = settings;
  const mutation = useMutation({
    mutationFn: async (passwordUpdateData: PasswordUpdateData) => {
      try {
        const response = await axios.put<PasswordUpdateResponse>(`${url}/user/password`, {
          body: JSON.stringify({
            currentPassword: passwordUpdateData.currentPassword,
            password: passwordUpdateData.password,
          }),
          currentPassword: passwordUpdateData.currentPassword,
          password: passwordUpdateData.password,
          // NOTE: This null fallback is just incase email is undefined because we need to send an email key on the body
          email: user?.email ?? null,
        });
        if (response.status === 200 && response.data.id === 1) {
          return response;
        }
        throw new Error('Error updating password');
      } catch (error: any) {
        if (error.response?.status === 400) {
          const passwordError: PasswordError = new Error(error.response.data.message);
          passwordError.error_code = error.response.data.error_code;
          throw passwordError;
        }
        throw error;
      }
    },
  });

  return mutation;
};

export default useUpdatePassword;
